/**
 * The five states a run is shown as, and how they are read off the API.
 *
 * The service reports `state` as `running`, `paused` or `finished`, and
 * `outcome` as `success`, `failure` or `cancelled` once a run has ended. The
 * screens show one word, so the pair is folded together here rather than in
 * each tab.
 *
 * `cancelled` is its own bucket: somebody stopping a run is neither the job
 * getting done nor Radar failing at it.
 *
 * Both halves can now be filtered and counted server-side, so a funnel built on
 * these is answerable — which it was not when this was first written.
 */

import {
	AOPExecution,
	AOPExecutionOutcome,
	AOPExecutionState,
} from '../../../types/AOPExecutionTypes';

export type AOPRunState = 'active' | 'paused' | 'successful' | 'unsuccessful' | 'cancelled';

export interface AOPRunStateChrome {
	label: string;
	/**
	 * Colours the label and, through `currentColor`, Oscar beside it.
	 *
	 * `successful` borrows `lifecycleChange.negative` because it is the only
	 * readable green in `tailwind.config.js` — every entry in the `green` scale
	 * is a pale fill, the darkest being green-5 at #9cd8b6. The token is named
	 * for a cost going down rather than for success, so this wants a proper
	 * token of its own; inventing one here is not ours to do.
	 */
	textClass: string;
	/**
	 * The surface a run's card sits on, so its state is legible before anything
	 * on it is read.
	 *
	 * Every one of these is an existing token. `purple-1` is the shade the design
	 * names for an active run; `character.dangerBg` is the only light red we
	 * have. The other two are approximations of a palette gap rather than
	 * choices: there is no light orange — `sunsetOrange` has only its 8 — so a
	 * paused run borrows `calendulaGold-1`, and `green` is a scale of pale fills
	 * with no tint made for a surface, so a successful one takes `green-2`.
	 * Inventing tokens for either is not ours to do; they want real ones.
	 *
	 * Cancelled takes `grey-5` rather than a lighter step because the page it
	 * sits on is already near-white: `grey-3` and `grey-4` are within a few
	 * points of it and the card disappeared. `grey-5` is the first shade in the
	 * scale that reads as a surface of its own, and the next one down is dark
	 * enough to fight the text.
	 *
	 * The `!` is part of the written class and not added at the call site:
	 * Tailwind generates a rule only for class names it finds spelled out in the
	 * source, so a `!` glued on by a template literal would ask for a rule that
	 * was never built. It is needed because antd's stylesheet loads after
	 * Tailwind's, so `.ant-card`'s own white wins on source order otherwise.
	 */
	bgClass: string;
	/** Oscar beats his wings only while something is actually happening. */
	isAnimated: boolean;
}

export const AOP_RUN_STATES: { [state in AOPRunState]: AOPRunStateChrome } = {
	active: {
		label: 'Active',
		textClass: 'text-purple-6',
		bgClass: '!bg-purple-1',
		isAnimated: true,
	},
	paused: {
		label: 'Paused',
		textClass: 'text-sunsetOrange-8',
		bgClass: '!bg-calendulaGold-1',
		isAnimated: false,
	},
	successful: {
		label: 'Successful',
		textClass: 'text-lifecycleChange-negative',
		bgClass: '!bg-green-2',
		isAnimated: false,
	},
	unsuccessful: {
		label: 'Unsuccessful',
		textClass: 'text-character-danger',
		bgClass: '!bg-character-dangerBg',
		isAnimated: false,
	},
	cancelled: {
		label: 'Cancelled',
		textClass: 'text-character-secondary',
		bgClass: '!bg-grey-5',
		isAnimated: false,
	},
};

export const runStateOf = (execution: AOPExecution): AOPRunState => {
	// All three come off the run itself now. `paused` is a state the service
	// keeps rather than a flag on the newest event, and `outcome` is lifted from
	// the finished event for us — so nothing here reads a snapshot to decide
	// what a row says.
	if (execution.state === 'paused') {
		return 'paused';
	}
	if (execution.state === 'running') {
		return 'active';
	}
	if (execution.outcome === 'success') {
		return 'successful';
	}
	if (execution.outcome === 'cancelled') {
		return 'cancelled';
	}
	// `failure`, and a finished run whose outcome never arrived: neither is a
	// success, and saying so is more use than saying nothing.
	return 'unsuccessful';
};

/** The order a funnel reads in: still going, then the three ways of ending. */
export const AOP_RUN_ORDER: AOPRunState[] = [
	'active',
	'paused',
	'successful',
	'unsuccessful',
	'cancelled',
];

export interface AOPRunStateFilters {
	state: AOPExecutionState;
	outcome?: AOPExecutionOutcome;
}

/**
 * What to ask the service for to get one of the five.
 *
 * Both endpoints take `state` and `outcome` and answer with a `count` of what
 * matches, so each of these is a question the service can answer on its own —
 * which it could not when the funnel was first built, and is why it only had
 * `running` and `finished` then.
 */
export const filtersForRunState = (runState: AOPRunState): AOPRunStateFilters => {
	if (runState === 'active') {
		return { state: 'running' };
	}
	if (runState === 'paused') {
		return { state: 'paused' };
	}
	if (runState === 'successful') {
		return { state: 'finished', outcome: 'success' };
	}
	if (runState === 'cancelled') {
		return { state: 'finished', outcome: 'cancelled' };
	}
	return { state: 'finished', outcome: 'failure' };
};

/** How many runs match each of the five. Absent until a count has arrived. */
export type AOPRunStateCounts = { [state in AOPRunState]?: number };

/**
 * Reads all five counts.
 *
 * `count` is per filter set — it is what the filters match, not what is on the
 * page — so there is one small request per stage. Each asks for a single item
 * and keeps only the number. A stage whose request fails is left without a
 * count rather than shown as zero, which would be a different claim.
 */
export const readRunStateCounts = (
	countFor: (filters: AOPRunStateFilters) => Promise<number>
): Promise<AOPRunStateCounts> =>
	Promise.all(
		AOP_RUN_ORDER.map((runState) =>
			countFor(filtersForRunState(runState))
				.then((count) => ({ runState, count }))
				.catch(() => null)
		)
	).then((results) => {
		const counts: AOPRunStateCounts = {};

		results.forEach((result) => {
			if (result) {
				counts[result.runState] = result.count;
			}
		});

		return counts;
	});
